import React from 'react';
import { Link } from 'react-router-dom';
import * as Icons from 'lucide-react';
import { Button } from '../components/ui/button';
import { useSiteContent } from '../context/SiteContentContext';

const Icon = ({ name, className }) => {
  const C = Icons[name] || Icons.Circle;
  return <C className={className} />;
};

const Services = () => {
  const { services: c } = useSiteContent();

  return (
    <div className="bg-white" data-testid="services-page">
      {/* Hero */}
      <section className="bg-gradient-to-r from-[#003366] to-[#004080] text-white py-20">
        <div className="container mx-auto px-4 text-center">
          <h1 className="text-5xl md:text-6xl font-bold mb-6 font-heading">{c.hero.heading}</h1>
          <p className="text-xl max-w-3xl mx-auto">{c.hero.subtext}</p>
        </div>
      </section>

      {/* Services Grid */}
      <section className="py-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {(c.services || []).map((service, index) => (
              <div key={service.id || index} className="relative rounded-2xl border-2 border-[#EAE5E0] hover:border-[#e74c3c] p-8 transition-all duration-300 hover:shadow-xl hover:-translate-y-1 bg-white">
                <div className="w-20 h-20 bg-gradient-to-br from-red-100 to-orange-100 rounded-full flex items-center justify-center mb-6">
                  <Icon name={service.icon || 'Circle'} className="w-10 h-10 text-[#e74c3c]" />
                </div>
                <h3 className="text-2xl font-bold mb-3 font-heading text-gray-900">{service.title}</h3>
                <p className="text-base text-gray-600 mb-6">{service.description}</p>
                <div className="space-y-3 mb-6">
                  <h4 className="font-semibold text-gray-900">Key Features:</h4>
                  <ul className="space-y-2">
                    {(service.features || []).map((feature, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-700">
                        <div className="w-2 h-2 bg-[#e74c3c] rounded-full mr-3 mt-1.5 flex-shrink-0" />
                        {feature}
                      </li>
                    ))}
                  </ul>
                </div>
                <Link to="/membership">
                  <Button className="w-full bg-[#e74c3c] hover:bg-[#c0392b]">Get Started</Button>
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4 text-[#003366] font-heading">{c.howItWorksHeading}</h2>
            <div className="w-24 h-1 bg-gradient-to-r from-[#e74c3c] to-[#c0392b] mx-auto" />
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {(c.steps || []).map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-gradient-to-br from-[#e74c3c] to-[#c0392b] text-white rounded-full flex items-center justify-center text-2xl font-bold mx-auto mb-4">
                  {item.step}
                </div>
                <h3 className="text-xl font-bold mb-2 text-gray-900">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#003366] to-[#004a7f] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">{c.cta.heading}</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">{c.cta.subtext}</p>
          <Link to="/membership">
            <Button size="lg" className="bg-white text-[#e74c3c] hover:bg-gray-100">{c.cta.button}</Button>
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Services;
